import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, extractApiError } from "../lib/api";
import StandaloneSystemHeader from "../components/branding/StandaloneSystemHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import ThemeToggle from "../components/theme/ThemeToggle";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => (searchParams.get("token") || "").trim(), [searchParams]);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!token) {
      setError("Der Link ist ungültig oder unvollständig.");
      return;
    }
    if (!password || !passwordConfirm) {
      setError("Bitte gib ein Passwort ein und bestätige es.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }
    if (password.length < 8) {
      setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }

    setLoading(true);
    try {
      await api.post("/api/auth/password-reset/confirm", {
        token,
        new_password: password,
        new_password_confirm: passwordConfirm,
      });
      setSuccess(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        setError(extractApiError(err, "Der Link ist ungültig oder abgelaufen."));
      } else if (status === 422) {
        setError("Bitte prüfe deine Eingaben.");
      } else {
        setError(extractApiError(err, "Es ist ein technischer Fehler aufgetreten. Bitte versuche es später erneut."));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-background text-foreground">
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-50" aria-hidden />
      <div className="pointer-events-none absolute inset-0 bg-radial-brand" aria-hidden />

      <div className="absolute top-4 right-4 z-30">
        <ThemeToggle />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <StandaloneSystemHeader />

          <Card className="backdrop-blur-xl">
            <CardContent className="p-6 sm:p-8">
              <h1 className="font-display text-2xl font-semibold tracking-tight">Passwort zurücksetzen</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Lege ein neues Passwort für dein FyreCRM-Konto fest.
              </p>

              {!success ? (
                <form className="space-y-4 mt-6" onSubmit={handleSubmit}>
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-password">Neues Passwort</Label>
                    <Input
                      id="reset-password"
                      type="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-password-confirm">Passwort bestätigen</Label>
                    <Input
                      id="reset-password-confirm"
                      type="password"
                      autoComplete="new-password"
                      value={passwordConfirm}
                      onChange={(event) => setPasswordConfirm(event.target.value)}
                      required
                    />
                  </div>

                  {error ? (
                    <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2.5">
                      {error}
                    </div>
                  ) : null}

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Wird gespeichert …" : "Passwort zurücksetzen"}
                  </Button>
                </form>
              ) : (
                <div className="mt-6 space-y-4">
                  <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2.5">
                    Dein Passwort wurde erfolgreich aktualisiert.
                  </div>
                  <Button asChild className="w-full">
                    <Link to="/login">Zur Anmeldung</Link>
                  </Button>
                </div>
              )}

              {!success ? (
                <div className="mt-5 text-sm text-muted-foreground">
                  Link ungültig oder abgelaufen?{" "}
                  <Link to="/passwort-vergessen" className="text-primary underline-offset-4 hover:underline">
                    Neuen Link anfordern
                  </Link>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
